// larcendaggerEffect.ts
import { Particle } from '../ParticleLayer'

const CONFIG = {
    slashColorInner: '#ffffff',
    slashColorOuter: '#c084fc', // Purple-400
    slashColorCore: '#7e22ce', // Purple-700
    smokeColor1: '#000000',
    smokeColor2: '#3b0764', // Very dark purple
    spritePath: '/assets/effects/larcendagger/sprite.svg',
    scale: 0.25 // Scale for 800x800 SVG to look good on canvas
}

// ----- Resource Caches -----
const spriteCache: Record<string, HTMLImageElement> = {}
const silhouetteCache: Record<string, HTMLCanvasElement> = {}
const gradientCache: Map<string, CanvasGradient> = new Map()

const getSprite = (src: string): HTMLImageElement => {
    if (!spriteCache[src]) {
        const img = new Image()
        img.src = src
        spriteCache[src] = img
    }
    return spriteCache[src]
}

// Pre-render a silhouette for the trail/ghosts to avoid expensive shadow filters
const getSilhouette = (img: HTMLImageElement, color: string): HTMLCanvasElement | null => {
    const key = `${img.src}-${color}`
    if (!silhouetteCache[key] && img.complete && img.naturalWidth > 0) {
        const canvas = document.createElement('canvas')
        canvas.width = img.naturalWidth
        canvas.height = img.naturalHeight
        const sctx = canvas.getContext('2d')
        if (sctx) {
            sctx.drawImage(img, 0, 0)
            sctx.globalCompositeOperation = 'source-in'
            sctx.fillStyle = color
            sctx.fillRect(0, 0, canvas.width, canvas.height)
            silhouetteCache[key] = canvas
        }
    }
    return silhouetteCache[key] || null
}

getSprite(CONFIG.spritePath) // Preload

// ----- Effect Data Definition -----
export type LarcendaggerData = {
    state: 'approach' | 'slash' | 'return' | 'fade'
    timeInState: number

    // Core physical position
    x: number
    y: number
    vx: number
    vy: number

    slashAngle: number

    trail: { x: number, y: number, angle: number }[]
    smoke: { x: number; y: number; vx: number; vy: number; life: number; maxLife: number; size: number; color: string }[]
    sparks: { x: number; y: number; vx: number; vy: number; life: number; color: string }[]
    souls: { x: number; y: number; vx: number; vy: number; life: number; size: number }[]
}

export type LarcendaggerEffect = {
    kind: 'larcendagger'
    t: number
    ox: number // Owner X (where the steal starts and ends)
    oy: number // Owner Y
    tx: number // Target X (where slash happens)
    ty: number // Target Y
    isOpponent: boolean
    data: LarcendaggerData
}

export function createLarcendaggerEffect(ox: number, oy: number, tx: number, ty: number, isOpponent: boolean = false): LarcendaggerEffect {
    return {
        kind: 'larcendagger',
        t: 0,
        ox, oy, tx, ty,
        isOpponent,
        data: {
            state: 'approach',
            timeInState: 0,
            x: ox,
            y: oy,
            vx: 0, vy: 0,
            slashAngle: Math.random() * Math.PI,
            trail: [],
            smoke: [], sparks: [], souls: []
        }
    }
}

// Helper for crescent slashes with caching
function drawCrescentSlash(ctx: CanvasRenderingContext2D, size: number, thickness: number, colorCore: string, colorEdge: string) {
    ctx.beginPath();
    ctx.moveTo(-size, 0); // Left tip
    ctx.quadraticCurveTo(0, thickness, size, 0); // Top curve
    ctx.quadraticCurveTo(0, -thickness, -size, 0); // Bottom curve
    ctx.closePath();

    const gradKey = `${thickness}-${colorCore}-${colorEdge}`
    let grad = gradientCache.get(gradKey)
    if (!grad) {
        grad = ctx.createLinearGradient(0, -thickness, 0, Math.max(0.1, thickness))
        grad.addColorStop(0, colorEdge)
        grad.addColorStop(0.5, colorCore)
        grad.addColorStop(1, colorEdge)
        gradientCache.set(gradKey, grad)
    }

    ctx.fillStyle = grad;
    // Removed shadowBlur here (expensive)
    ctx.fill();
}

export function renderLarcendaggerEffect(effect: LarcendaggerEffect, ctx: CanvasRenderingContext2D): boolean {
    const d = effect.data
    const dt = 16.7 // roughly 1 frame
    d.timeInState += dt

    let globalAlpha = 1.0

    // ----- State Machine & Physics -----
    if (d.state === 'approach') {
        const TARGET_TIME = 300; 
        const prog = Math.min(1, d.timeInState / TARGET_TIME);
        const ease = prog * prog * prog;

        const nextX = effect.ox + (effect.tx - effect.ox) * ease;
        const nextY = effect.oy + (effect.ty - effect.oy) * ease;

        d.vx = nextX - d.x;
        d.vy = nextY - d.y;
        d.x = nextX;
        d.y = nextY;

        // Trail updates
        const speed = Math.sqrt(d.vx * d.vx + d.vy * d.vy);
        const leanAngle = Math.min(Math.PI / 4, speed * 0.02);
        d.trail.unshift({ x: d.x, y: d.y, angle: leanAngle });
        if (d.trail.length > 8) d.trail.pop(); 

        // Smoke (Frequency slightly reduced for optimization)
        if (Math.random() > 0.4) {
            d.smoke.push({
                x: d.x + (Math.random() - 0.5) * 40,
                y: d.y + (Math.random() - 0.5) * 40,
                vx: -d.vx * 0.1 + (Math.random() - 0.5) * 2,
                vy: -d.vy * 0.1 + (Math.random() - 0.5) * 2,
                life: 0, maxLife: 20 + Math.random() * 15,
                size: 20 + Math.random() * 15,
                color: Math.random() > 0.5 ? CONFIG.smokeColor1 : CONFIG.smokeColor2
            })
        }

        if (prog >= 1) {
            d.state = 'slash';
            d.timeInState = 0;
            d.trail = []; 

            // Impact Blast
            for (let i = 0; i < 15; i++) { // Reduced count from 20
                d.smoke.push({
                    x: effect.tx + (Math.random() - 0.5) * 80,
                    y: effect.ty + (Math.random() - 0.5) * 80,
                    vx: (Math.random() - 0.5) * 15,
                    vy: (Math.random() - 0.5) * 15,
                    life: 0, maxLife: 30 + Math.random() * 20,
                    size: 25 + Math.random() * 25,
                    color: Math.random() > 0.5 ? CONFIG.smokeColor1 : CONFIG.smokeColor2
                })
            }
            // Sparks
            for (let i = 0; i < 15; i++) {
                const ang = d.slashAngle + (Math.random() - 0.5) * Math.PI / 2
                const spd = 6 + Math.random() * 12
                d.sparks.push({
                    x: effect.tx, y: effect.ty,
                    vx: Math.cos(ang) * spd,
                    vy: Math.sin(ang) * spd,
                    life: 1.0, color: Math.random() > 0.5 ? CONFIG.slashColorOuter : '#ffffff'
                })
            }
        }
    } else if (d.state === 'slash') {
        if (d.timeInState > 300) {
            d.state = 'return'
            d.timeInState = 0
            for (let i = 0; i < 8; i++) {
                d.souls.push({
                    x: effect.tx + (Math.random() - 0.5) * 40,
                    y: effect.ty + (Math.random() - 0.5) * 40,
                    vx: (Math.random() - 0.5) * 6,
                    vy: (Math.random() - 0.5) * 6,
                    life: 1.0, size: 4 + Math.random() * 5
                })
            }
        }
    } else if (d.state === 'return') {
        const TARGET_TIME = 600;
        const prog = Math.min(1, d.timeInState / TARGET_TIME);
        const ease = 1 - Math.pow(1 - prog, 3);

        const nextX = effect.tx + (effect.ox - effect.tx) * ease;
        const nextY = effect.ty + (effect.oy - effect.ty) * ease;

        d.vx = nextX - d.x;
        d.vy = nextY - d.y;
        d.x = nextX;
        d.y = nextY;

        const speed = Math.sqrt(d.vx * d.vx + d.vy * d.vy);
        const leanAngle = Math.min(Math.PI / 4, speed * 0.02);
        d.trail.unshift({ x: d.x, y: d.y, angle: leanAngle });
        if (d.trail.length > 8) d.trail.pop();

        d.souls.forEach(s => {
            const dx = d.x - s.x;
            const dy = d.y - s.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist > 5) {
                s.vx += (dx / dist) * 2.0;
                s.vy += (dy / dist) * 2.0;
            }
            s.vx *= 0.85; s.vy *= 0.85;
            s.x += s.vx; s.y += s.vy;
            s.life = Math.min(1.0, s.life + 0.05);

            if (Math.random() > 0.6) {
                d.sparks.push({
                    x: s.x, y: s.y,
                    vx: -s.vx * 0.2 + (Math.random() - 0.5) * 2, vy: -s.vy * 0.2 + (Math.random() - 0.5) * 2,
                    life: 0.5, color: '#e9d5ff'
                })
            }
        });

        if (prog >= 1) {
            d.state = 'fade';
            d.timeInState = 0;
            d.trail = [];
            for (let i = 0; i < d.souls.length; i++) {
                d.smoke.push({
                    x: effect.ox, y: effect.oy,
                    vx: (Math.random() - 0.5) * 8, vy: (Math.random() - 0.5) * 8,
                    life: 0, maxLife: 35, size: 25, color: '#a855f7'
                })
            }
            d.souls = [];
        }
    } else if (d.state === 'fade') {
        const FADE_TIME = 400;
        globalAlpha = Math.max(0, 1 - (d.timeInState / FADE_TIME));
        if (d.timeInState > FADE_TIME && d.smoke.length === 0 && d.sparks.length === 0) {
            return false;
        }
    }

    // Update Particles (Optimized loop)
    for (let i = d.smoke.length - 1; i >= 0; i--) {
        const p = d.smoke[i];
        p.life++;
        p.x += p.vx;
        p.y += p.vy;
        p.size += 0.5;
        p.vx *= 0.88;
        p.vy *= 0.88;
        if (p.life >= p.maxLife) {
            d.smoke[i] = d.smoke[d.smoke.length - 1];
            d.smoke.pop();
        }
    }

    for (let i = d.sparks.length - 1; i >= 0; i--) {
        const p = d.sparks[i];
        p.x += p.vx;
        p.y += p.vy;
        p.life -= 0.05;
        if (p.life <= 0) {
            d.sparks[i] = d.sparks[d.sparks.length - 1];
            d.sparks.pop();
        }
    }

    // ----- RENDERING -----
    ctx.save()
    ctx.globalAlpha = globalAlpha;

    // 1. Draw Smoke (Grouped and batched where possible)
    ctx.globalCompositeOperation = 'source-over'
    d.smoke.forEach(p => {
        const alpha = Math.min(0.7, (1 - p.life / p.maxLife)) * globalAlpha
        ctx.globalAlpha = alpha
        ctx.fillStyle = p.color
        ctx.beginPath()
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2)
        ctx.fill()
    })
    ctx.globalAlpha = globalAlpha

    // 2. Draw Ghostly Afterimages (Optimized with Silhouette Cache)
    const img = getSprite(CONFIG.spritePath)
    if (img.complete && img.naturalWidth > 0 && d.trail.length > 0) {
        const silhouette = getSilhouette(img, '#4c1d95')
        if (silhouette) {
            ctx.globalCompositeOperation = 'source-over'
            d.trail.forEach((pos, idx) => {
                const alpha = (1 - (idx / d.trail.length)) * 0.4 * globalAlpha;
                ctx.globalAlpha = alpha;
                ctx.save()
                ctx.translate(pos.x, pos.y)
                let isFacingLeft = effect.tx < effect.ox;
                if (d.state === 'return') isFacingLeft = effect.ox < effect.tx;
                if (isFacingLeft) ctx.scale(-1, 1);
                ctx.rotate(pos.angle);
                ctx.scale(CONFIG.scale, CONFIG.scale);
                ctx.drawImage(silhouette, -silhouette.width / 2, -silhouette.height / 2)
                ctx.restore()
            });
        }
    }
    ctx.globalAlpha = globalAlpha

    // 3. Draw Main Rogue Sprite
    if (d.state !== 'fade' || globalAlpha > 0) {
        if (img.complete && img.naturalWidth > 0) {
            ctx.save()
            ctx.translate(d.x, d.y)
            let scale = CONFIG.scale;
            if (d.state === 'approach' && d.timeInState < 100) {
                scale = Math.max(0.01, CONFIG.scale * (d.timeInState / 100));
            }
            let isFacingLeft = effect.tx < effect.ox;
            if (d.state === 'return') isFacingLeft = effect.ox < effect.tx;
            if (isFacingLeft) ctx.scale(-1, 1);
            const speed = Math.sqrt(d.vx * d.vx + d.vy * d.vy);
            const leanAngle = Math.min(Math.PI / 4, speed * 0.02);
            ctx.rotate(leanAngle);
            ctx.scale(scale, scale);
            
            // Simplified shadow offset instead of blur
            const shadowSil = getSilhouette(img, 'rgba(0,0,0,0.4)')
            if (shadowSil) {
                ctx.drawImage(shadowSil, -shadowSil.width / 2 + 10, -shadowSil.height / 2 + 15)
            }

            ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2)
            ctx.restore()
        }
    }

    // 4. Draw Souls (Optimized Glow Simulation)
    ctx.globalCompositeOperation = 'lighter'
    d.souls.forEach(s => {
        const alpha = Math.max(0, s.life * globalAlpha)
        const size = s.size * alpha
        ctx.fillStyle = `rgba(147, 51, 234, ${alpha * 0.3})`;
        ctx.beginPath(); ctx.arc(s.x, s.y, size * 2.5, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = `rgba(168, 85, 247, ${alpha})`;
        ctx.beginPath(); ctx.arc(s.x, s.y, size, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`;
        ctx.beginPath(); ctx.arc(s.x, s.y, size * 0.4, 0, Math.PI * 2); ctx.fill();
    })

    // 5. Draw Awesome Crescent Slash (at target)
    if (d.state === 'slash') {
        const t = d.timeInState / 300 
        ctx.globalCompositeOperation = 'lighter'
        ctx.save()
        ctx.translate(effect.tx, effect.ty)
        ctx.rotate(d.slashAngle)

        const len1 = t < 0.2 ? (t / 0.2) * 200 : 200;
        const op1 = t > 0.6 ? 1 - ((t - 0.6) / 0.4) : 1;
        ctx.globalAlpha = op1 * globalAlpha;
        drawCrescentSlash(ctx, len1, 25, CONFIG.slashColorInner, CONFIG.slashColorCore);

        if (t > 0.1) {
            const t2 = (t - 0.1) / 0.9;
            const len2 = t2 < 0.2 ? (t2 / 0.2) * 160 : 160;
            const op2 = t2 > 0.6 ? 1 - ((t2 - 0.6) / 0.4) : 1;
            ctx.globalAlpha = op2 * globalAlpha;
            ctx.rotate(Math.PI / 1.5); 
            drawCrescentSlash(ctx, len2, 20, CONFIG.slashColorInner, CONFIG.slashColorOuter);
        }
        ctx.restore()
    }

    // 6. Draw Sparks
    ctx.globalCompositeOperation = 'lighter'
    d.sparks.forEach(p => {
        ctx.globalAlpha = p.life * globalAlpha
        ctx.fillStyle = p.color
        ctx.beginPath()
        ctx.arc(p.x, p.y, 2 + p.life * 2, 0, Math.PI * 2)
        ctx.fill()
    })

    ctx.restore()
    return true
}
